import { StyleSheet } from "react-native";
import { Image, View } from "react-native-web";
import { goBack } from "util/navigate";
import React, { useState } from "react";
import { DEPRECATED_useModulePublicData, useDatastore, useInstanceKey, useStructureKey } from "util/datastore";
import { PadBox, Pad, HorizBox, ShadowBox } from 'component/basics';
import { ConversationScreen } from "component/screen";
import { LoadingScreen } from "component/banner";
import { CTAButton } from 'component/button';
import { Heading, TextField, UtilityText } from 'component/text';
import { FormField } from 'component/form';
import { colorBorderPrimary, colorSurfacePrimary, colorTextPrimary, colorWhite } from 'component/color';
import { Information, NoImage } from "@carbon/icons-react";
import { useHasCapability } from 'component/admin';
import { Banner } from 'component/banner';
import { DocumentLevelTooltip } from "./tooltip";
import { ToggleWithLabelWidget } from "./form";

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function ChannelEditorScreen() {
    const datastore = useDatastore();
    const structureKey = useStructureKey();
    const instanceKey = useInstanceKey();
    const channel = DEPRECATED_useModulePublicData("channel", ["all", instanceKey]);

    const [imageUrl, setImageUrl] = useState(null);
    const [channelName, setChannelName] = useState(null);
    const [error, setError] = useState(null);
    const [isDefault, setIsDefault] = useState();

    const isNewChannel = structureKey !== "channel" && instanceKey === "one";

    const canCreateChannel = useHasCapability("channel/create");
    const canEditChannel = useHasCapability("channel/edit");

    if (isNewChannel && !canCreateChannel || !isNewChannel && !canEditChannel) {
        return (
            <Banner>
                <UtilityText label="You do not have access to this feature" />
            </Banner>
        );
    }
    
    if (!isNewChannel && !channel) {
        return <LoadingScreen label="Loading" />;
    }

    // TODO: Show success and error toasts. I can't figure out why they don't show up here.
    async function createChannel() {
        if (imageUrl?.length > 0 && channelName?.length > 0) {
            await datastore.callServerAsync("channel", "createChannel", {
                photoUrl: imageUrl,
                name: channelName,
                isDefault: isDefault ?? false,
            });
            goBack();
        } else {
            setError(true);
        }
    }

    async function editChannel() {
        if (
            ((imageUrl && imageUrl.length > 0) || channel.photoUrl.length > 0) &&
            ((channelName && channelName.length > 0) || channel.name.length > 0)
        ) {
            await datastore.callServerAsync("channel", "updateChannel", {
                key: instanceKey,
                photoUrl: imageUrl,
                name: channelName,
                isDefault: isDefault ?? channel.isDefault,
            });
            goBack();
        }
    }

    return (
        <ConversationScreen>
            <PadBox horiz={20} vert={20}>
                <View style={{"justifyContent":"center",alignItems:"center"}}>
                    <Heading label={isNewChannel ? "Create Channel" : "Edit Channel"} type="large" />
                    <Pad size={16} />
                    {isNewChannel ? (
                        <ChannelEditor
                            imageUrl={imageUrl}
                            setImageUrl={setImageUrl}
                            channelName={channelName}
                            setChannelName={setChannelName}
                            isDefault={isDefault ?? false}
                            setIsDefault={setIsDefault}
                            error={error}
                        />
                    ) : (
                        <ChannelEditor
                            imageUrl={imageUrl ?? channel.photoUrl}
                            setImageUrl={setImageUrl}
                            channelName={channelName ?? channel.name}
                            setChannelName={setChannelName}
                            isDefault={isDefault ?? channel.isDefault}
                            setIsDefault={setIsDefault}
                        />
                    )}

                    <Pad size={16} />
                    <View>
                    <CTAButton
                        label={isNewChannel ? "Create Channel" : "Save"}
                        onPress={() => (isNewChannel ? createChannel() : editChannel())}
                    />
                    </View>
                </View>
            </PadBox>
        </ConversationScreen>
    );
}

const ChannelEditorStyle = StyleSheet.create({
    formContainer: {
        justifyContent:"center",
        alignItems:"center",
        gap: 16,
    }
});

export function ChannelEditor({ imageUrl, setImageUrl, channelName, setChannelName, isDefault, setIsDefault, error}) {
    const s = ChannelEditorStyle;

    return (
        <View style={s.formContainer}>
            {imageUrl != null && imageUrl !== "" ? <ChannelImage imageUrl={imageUrl} /> : <ChannelImage />}
            <FormField label={"Image URL"} required={true}>
                <TextField
                    testID={"image-url"}
                    multiline={false}
                    error={error}
                    value={imageUrl}
                    onChange={(url) => setImageUrl(url)}
                />
            </FormField>
            <FormField label={"Channel Name"} required={true}>
                <TextField
                    testID={"channel-name"}
                    multiline={false}
                    error={error}
                    value={channelName}
                    onChange={(name) => setChannelName(name)}
                />
            </FormField>
            {/* TODO: Add a warning if the user is about to revert the existing default channel */}
            <ToggleWithLabelWidget
                testID="default-channel-toggle"
                ariaText={"Use as default channel"}
                labelWidget={DefaultChannelToggleWidget}
                value={isDefault}
                onChange={(value) => setIsDefault(value)}
            />
        </View>
    );
}

export function DefaultChannelToggleWidget() {
    return (
        <HorizBox center>
            <UtilityText label={"Use as default channel"} />
            <Pad size={8} />
            <DefaultChannelTooltip />
        </HorizBox>
    );
}

export function DefaultChannelTooltip() {
    return (
        <DocumentLevelTooltip
            tooltipWidget={
                <ShadowBox>
                    <Banner color={colorSurfacePrimary}>
                        <View style={{ width: 300 }}>
                            <UtilityText label="All admin users have access to the default channel." />
                            <Pad size={8} />
                            <UtilityText label="New questions and topics that are not assigned to a specific channel will be assigned to the default channel." />
                            <Pad size={8} />
                            <UtilityText label="There can only be one default channel." />
                        </View>
                    </Banner>
                </ShadowBox>
            }
        >
            <Information color={colorTextPrimary}/>
        </DocumentLevelTooltip>
    );
}

const ChannelImageStyle = StyleSheet.create({
    noImageIcon: {
        alignItems: "center",
        justifyContent: "center",
        opacity: 0.25
    }
})

export function ChannelImage({ imageUrl, showBorder = false, width = 120, height = 120, icon = null, borderColor = colorBorderPrimary }) {
    const s = ChannelImageStyle;

    return (
        <View
            style={{
                width: width,
                height: height,
                color: colorTextPrimary,
                borderRadius: "50%",
                gap: 8,
                boxShadow: !showBorder ? "0px 0px 1px 1px " + colorBorderPrimary : "0px 0px 6px 2px " + borderColor,
                elevation: showBorder ? 8 : 5,
                transform: showBorder ? "scale(1)" : "scale(0.9)",
            }}
        >
            {!!(imageUrl && imageUrl !== "") && (
                <Image
                    source={imageUrl}
                    style={{
                        width: width,
                        height: height,
                        borderRadius: "50%",
                        border: showBorder ? "2px solid " + borderColor : "transparent",
                    }}
                />
            )}

            {icon && React.createElement(icon, { width: width, height: height, color: colorTextPrimary })}

            {/* Show "no image" icon if there is no image and no other icon */}
            {!icon && (!imageUrl || imageUrl === "") && (
                <View style={[s.noImageIcon, { width: width, height: height }]}>
                    {React.createElement(NoImage, { width: width / 1.5, height: height / 1.5 })}
                </View>
            )}
        </View>
    );
}